import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Plus,
  CalendarCheck,
  CalendarBlank,
  Check,
  X,
  Storefront,
} from "@phosphor-icons/react";
import { format, parseISO, isToday, isTomorrow, isPast } from "date-fns";
import { TopHeader } from "@/components/TopHeader";

export default function SchedulePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCustomer = searchParams.get("customer");

  const [schedules, setSchedules] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const [formData, setFormData] = useState({
    customer_id: preselectedCustomer || "",
    scheduled_date: null,
    notes: "",
  });

  useEffect(() => {
    fetchData();
    if (preselectedCustomer) {
      setDialogOpen(true);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, customersRes] = await Promise.all([
        axios.get(`${API}/schedules`),
        axios.get(`${API}/customers`),
      ]);
      setSchedules(schedulesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error("Failed to load schedules");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error("Please select a customer");
      return;
    }
    if (!formData.scheduled_date) {
      toast.error("Please select a date");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/schedules`, {
        customer_id: formData.customer_id,
        scheduled_date: formData.scheduled_date.toISOString(),
        notes: formData.notes || null,
      });
      toast.success("Visit scheduled successfully");
      setDialogOpen(false);
      setFormData({ customer_id: "", scheduled_date: null, notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule visit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (scheduleId, status) => {
    try {
      await axios.put(`${API}/schedules/${scheduleId}`, { status });
      toast.success(`Visit marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (scheduleId) => {
    try {
      await axios.delete(`${API}/schedules/${scheduleId}`);
      toast.success("Schedule deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete schedule");
    }
  };

  const formatScheduleDate = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return "Today";
      if (isTomorrow(date)) return "Tomorrow";
      return format(date, "MMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (schedule) => {
    const date = parseISO(schedule.scheduled_date);
    if (schedule.status === "completed") {
      return <span className="badge badge-success">Completed</span>;
    }
    if (schedule.status === "cancelled") {
      return <span className="badge badge-danger">Cancelled</span>;
    }
    if (isPast(date) && schedule.status === "pending") {
      return <span className="badge badge-warning">Overdue</span>;
    }
    return <span className="badge badge-info">Pending</span>;
  };

  const pendingSchedules = schedules.filter((s) => s.status === "pending");
  const completedSchedules = schedules.filter((s) => s.status !== "pending");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="schedule-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="schedule-page">
      {/* Header */}
      <TopHeader title="Schedule" subtitle="PLANNING">
        <Button
          onClick={() => setDialogOpen(true)}
          className="h-10 px-4 bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold rounded-full"
          data-testid="add-schedule-button"
        >
          <Plus size={20} weight="bold" className="mr-1" />
          Add
        </Button>
      </TopHeader>

      <div className="p-4 space-y-6">
        {/* Pending Visits */}
        <div className="animate-fade-in-up">
          <h2 className="font-bold text-[#0A0A0A] mb-3" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Upcoming Visits ({pendingSchedules.length})
          </h2>

          {pendingSchedules.length > 0 ? (
            <div className="card-flat overflow-hidden">
              {pendingSchedules.map((schedule, index) => (
                <div
                  key={schedule.id}
                  className="p-4 border-b border-[#E5E7EB] last:border-b-0"
                  data-testid={`schedule-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#DBEAFE] flex items-center justify-center flex-shrink-0">
                      <CalendarCheck size={20} className="text-[#0055FF]" weight="fill" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-1">
                        <button
                          onClick={() => navigate(`/customers/${schedule.customer_id}`)}
                          className="font-semibold text-[#0A0A0A] hover:text-[#0055FF] text-left"
                        >
                          {schedule.customer_name}
                        </button>
                        {getStatusBadge(schedule)}
                      </div>
                      <p className="text-sm text-[#4B5563]">
                        {formatScheduleDate(schedule.scheduled_date)}
                      </p>
                      {schedule.notes && (
                        <p className="text-sm text-[#4B5563] mt-1">{schedule.notes}</p>
                      )}
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusUpdate(schedule.id, "completed")}
                          className="h-8 px-3 text-[#10B981] border-[#10B981] hover:bg-[#D1FAE5]"
                          data-testid={`complete-schedule-${index}`}
                        >
                          <Check size={16} className="mr-1" />
                          Complete
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusUpdate(schedule.id, "cancelled")}
                          className="h-8 px-3 text-[#EF4444] border-[#EF4444] hover:bg-[#FEE2E2]"
                          data-testid={`cancel-schedule-${index}`}
                        >
                          <X size={16} className="mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state card-flat" data-testid="schedule-empty">
              <CalendarBlank size={48} className="text-[#D1D5DB] mb-2" />
              <h3 className="empty-state-title">No Upcoming Visits</h3>
              <p className="empty-state-text">Schedule a visit to stay organized</p>
              <Button
                onClick={() => setDialogOpen(true)}
                className="mt-4 bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold rounded-full"
                data-testid="add-first-schedule-button"
              >
                <Plus size={20} weight="bold" className="mr-1" />
                Schedule Visit
              </Button>
            </div>
          )}
        </div>

        {/* Past/Completed Visits */}
        {completedSchedules.length > 0 && (
          <div className="animate-fade-in-up stagger-1">
            <h2 className="font-bold text-[#0A0A0A] mb-3" style={{ fontFamily: "'Chivo', sans-serif" }}>
              Past Visits ({completedSchedules.length})
            </h2>

            <div className="card-flat overflow-hidden">
              {completedSchedules.map((schedule, index) => (
                <div
                  key={schedule.id}
                  className="p-4 border-b border-[#E5E7EB] last:border-b-0 opacity-60"
                  data-testid={`past-schedule-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#F3F4F6] flex items-center justify-center">
                      <Storefront size={20} className="text-[#9CA3AF]" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-[#4B5563]">{schedule.customer_name}</p>
                      <p className="text-sm text-[#9CA3AF]">
                        {formatScheduleDate(schedule.scheduled_date)}
                      </p>
                    </div>
                    {getStatusBadge(schedule)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Schedule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Visit</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[#0A0A0A] font-medium">Customer *</Label>
              <Select
                value={formData.customer_id}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, customer_id: value }))
                }
              >
                <SelectTrigger className="mt-1.5 h-12 border-[#E5E7EB]" data-testid="schedule-customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#0A0A0A] font-medium">Date *</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full mt-1.5 h-12 justify-start text-left font-normal border-[#E5E7EB]"
                    data-testid="schedule-date-picker"
                  >
                    <CalendarBlank size={20} className="mr-2 text-[#9CA3AF]" />
                    {formData.scheduled_date ? (
                      format(formData.scheduled_date, "PPP")
                    ) : (
                      <span className="text-[#9CA3AF]">Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.scheduled_date}
                    onSelect={(date) => {
                      setFormData((prev) => ({ ...prev, scheduled_date: date }));
                      setCalendarOpen(false);
                    }}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="notes" className="text-[#0A0A0A] font-medium">
                Notes (Optional)
              </Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Any notes for this visit..."
                rows={2}
                className="mt-1.5 border-[#E5E7EB] resize-none"
                data-testid="schedule-notes-input"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1"
                data-testid="cancel-schedule-form"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[#0055FF] hover:bg-[#0040CC] text-white"
                data-testid="submit-schedule-button"
              >
                {submitting ? "Scheduling..." : "Schedule Visit"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
